import {ServerWebExchangeMatcher} from './util/matchers.js';
import {HttpStatus} from '../http/status.js';
import getLogger from '../logger.js';
import type {HttpRequest, ReadonlyHttpHeaders} from '../../types/web/http';
import type {ServerHttpResponse, ServerWebExchange} from '../../types/web/server';
import {IOGateway} from '@interopio/gateway';

function isSameOrigin(request: HttpRequest<ReadonlyHttpHeaders>) {
    const origin = request.headers.one('origin');
    if (origin === undefined) {
        return true;
    }
    const url = request.URL;
    const actualProtocol = url.protocol;
    const actualHost = url.host;

    const originUrl = URL.parse(origin);

    const originHost = originUrl?.host;
    const originProtocol = originUrl?.protocol;
    return actualProtocol === originProtocol
        && actualHost === originHost;
}

/**
 * Returns `true` if the request is a valid CORS one by checking `Origin` header presence and ensuring origins differ.
 */
export function isCorsRequest(request: HttpRequest<ReadonlyHttpHeaders>): boolean {
    return request.headers.has('origin') && !isSameOrigin(request);

}

export function isPreFlightRequest(request: HttpRequest): boolean {
    return request.method === 'OPTIONS'
        && request.headers.has('origin')
        && request.headers.has('access-control-request-method');
}

const VARY_HEADERS: readonly string[] = ['Origin', 'Access-Control-Request-Method', 'Access-Control-Request-Headers'];

/**
 * Processes a request given a {@link CorsConfig}.
 *
 * @param exchange the current exchange
 * @param config the CORS configuration to use, possibly `undefined` in which case pre-flight requests are rejected, but all others allowed
 * @returns `false` if the request is rejected, `true` otherwise
 */
export const processRequest: CorsProcessor = (exchange: ServerWebExchange, config?: CorsConfig): boolean => {
    const {request, response} = exchange;
    const responseHeaders = response.headers;

    if (!responseHeaders.has('Vary')) {
        responseHeaders.set('Vary', VARY_HEADERS.join(', '));
    }
    else {
        const varyHeaders = responseHeaders.list('Vary');
        for (const header of VARY_HEADERS) {
            if (!varyHeaders.find(h => h === header)) {
                varyHeaders.push(header);
            }
        }
        responseHeaders.set('Vary', varyHeaders.join(', '));
    }

    try {
        if (!isCorsRequest(request)) {
            return true;
        }
    }
    catch (e) {
        if(logger.enabledFor('debug')) {
            logger.debug(`reject: origin is malformed`);
        }
        rejectRequest(response);
        return false;
    }

    if (responseHeaders.has('access-control-allow-origin')) {
        logger.trace(`skip: already contains "Access-Control-Allow-Origin"`);
        return true;
    }

    const preFlightRequest = isPreFlightRequest(request);

    if (config) {
        return handleInternal(exchange, config, preFlightRequest);
    }
    if (preFlightRequest) {
        rejectRequest(response);
        return false;
    }
    return true;
}

export type CorsConfig = Readonly<{
    allowOrigins?: '*' | IOGateway.Filtering.Matcher[]
    allowMethods?:'*' | string[]
    allowHeaders?: '*' | string[]
    exposeHeaders?: string[]
    allowCredentials?: boolean
    allowPrivateNetwork?:  boolean
    maxAge?: number
}>;

const DEFAULT_PERMIT_ALL = ['*'];
const DEFAULT_PERMIT_METHODS = ['GET', 'HEAD', 'POST'];
export const PERMIT_DEFAULT_CONFIG: CorsConfig = {
    allowOrigins: DEFAULT_PERMIT_ALL,
    allowMethods: DEFAULT_PERMIT_METHODS,
    allowHeaders: DEFAULT_PERMIT_ALL,
    maxAge: 1800 // 30 minutes
}
export /*testing*/ function validateCorsConfig(config?: CorsConfig): CorsConfig | undefined {
    if (config) {
        const allowHeaders = config.allowHeaders;
        if (allowHeaders && allowHeaders !== ALL) {
            config = {
                ...config,
                allowHeaders: allowHeaders.map(header => header.toLowerCase())
            };
        }
        const allowOrigins = config.allowOrigins;
        if (allowOrigins) {
            if (allowOrigins === '*') {
                validateAllowCredentials(config);
                validateAllowPrivateNetwork(config);
            }
            else {
                config = {
                    ...config,
                    allowOrigins: allowOrigins.map(origin => {
                        if (typeof origin === 'string' && origin !== ALL) {
                            origin = IOGateway.Filtering.regexify(origin);
                            if (typeof origin === 'string') {
                                // exact match
                                return trimTrailingSlash(origin).toLowerCase();
                            }
                        }
                        return origin;
                    })
                };
            }
        }
        return config;
    }
}

function combine<T = (string | IOGateway.Filtering.Matcher)>(source?: '*' | T[], other?: '*' | T[]): T[] | undefined {
    if (other === undefined) {
        return source !== undefined ? (source === ALL ? [ALL as T] : source) : [];
    }
    if (source === undefined) {
        return other === ALL ? [ALL as T] : other;
    }
    if (source == DEFAULT_PERMIT_ALL || source === DEFAULT_PERMIT_METHODS) {
        return other === ALL ? [ALL as T] : other;
    }
    if (other == DEFAULT_PERMIT_ALL || other === DEFAULT_PERMIT_METHODS) {
        return source === ALL ? [ALL as T] : source;
    }
    if (source === ALL || source.includes(ALL as T) || other === ALL || other.includes(ALL as T)) {
        return [ALL as T];
    }
    const combined = new Set<T>();
    source.forEach((v) => combined.add(v));
    other.forEach((v) => combined.add(v));
    return Array.from(combined);
}
export const combineCorsConfig = (source: CorsConfig, other?: CorsConfig): CorsConfig => {
    if (other === undefined) {
        return source;
    }
    const config: CorsConfig = {
        allowOrigins: combine(source.allowOrigins, other?.allowOrigins),
        allowMethods: combine(source.allowMethods, other?.allowMethods),
        allowHeaders: combine(source.allowHeaders, other?.allowHeaders),
        exposeHeaders: combine(source.exposeHeaders, other?.exposeHeaders),
        allowCredentials: other?.allowCredentials ?? source.allowCredentials,
        allowPrivateNetwork: other?.allowPrivateNetwork ?? source.allowPrivateNetwork,
        maxAge: other?.maxAge ?? source.maxAge
    }
    return config;

}

export type CorsConfigSource = (exchange: ServerWebExchange) => Promise<CorsConfig | undefined>;
export type CorsProcessor = (exchange: ServerWebExchange, config?: CorsConfig) => boolean;

const corsFilter = (opts: {corsConfigSource: CorsConfigSource, corsProcessor?: CorsProcessor}) => {
    const source = opts.corsConfigSource;
    const processor = opts.corsProcessor ?? processRequest;
    return async (ctx: ServerWebExchange, next: () => Promise<void>) => {
        const config = await source(ctx);
        const isValid = processor(ctx, config);
        if (!isValid || isPreFlightRequest(ctx.request)) {
            return;
        } else {
            await next();
        }
    };
}

export default corsFilter;


const logger = getLogger('cors');

function rejectRequest(response: ServerHttpResponse) {
    response.setStatusCode(HttpStatus.FORBIDDEN);
}

function handleInternal(exchange: ServerWebExchange,
                        config: CorsConfig, preFlightRequest: boolean): boolean {
    const {request, response} = exchange;
    const responseHeaders = response.headers;

    const requestOrigin = request.headers.one('origin');
    const allowOrigin = checkOrigin(config, requestOrigin);

    if (allowOrigin === undefined) {
        if (logger.enabledFor('debug')) {
            logger.debug(`reject: '${requestOrigin}' origin is not allowed`);
        }
        rejectRequest(response);
        return false;
    }

    const requestMethod = getMethodToUse(request, preFlightRequest);
    const allowMethods = checkMethods(config, requestMethod);
    if (allowMethods === undefined) {
        if (logger.enabledFor('debug')) {
            logger.debug(`reject: HTTP '${requestMethod}' is not allowed`);
        }
        rejectRequest(response);
        return false;
    }

    const requestHeaders = getHeadersToUse(request, preFlightRequest);
    const allowHeaders = checkHeaders(config, requestHeaders);
    if (preFlightRequest && allowHeaders === undefined) {
        if (logger.enabledFor('debug')) {
            logger.debug(`reject: headers '${requestHeaders}' are not allowed`);
        }
        rejectRequest(response);
        return false;
    }

    responseHeaders.set('Access-Control-Allow-Origin', allowOrigin);

    if (preFlightRequest) {
        responseHeaders.set('Access-Control-Allow-Methods', allowMethods.join(','));

    }
    if (preFlightRequest && allowHeaders !== undefined && allowHeaders.length  > 0) {
        responseHeaders.set('Access-Control-Allow-Headers', allowHeaders.join(', '));
    }
    const exposeHeaders = config.exposeHeaders;
    if (exposeHeaders && exposeHeaders.length > 0) {
        responseHeaders.set('Access-Control-Expose-Headers', exposeHeaders.join(', '));
    }
    if (config.allowCredentials) {
        responseHeaders.set('Access-Control-Allow-Credentials', 'true');
    }
    if (config.allowPrivateNetwork && request.headers.one('access-control-request-private-network') === 'true') {
        responseHeaders.set('Access-Control-Allow-Private-Network', 'true');
    }

    if (preFlightRequest && config.maxAge !== undefined) {
        responseHeaders.set('Access-Control-Max-Age', config.maxAge.toString());
    }
    return true;
}

const ALL = '*';
const DEFAULT_METHODS = ['GET', 'HEAD'];

function validateAllowCredentials(config: CorsConfig) {
    if (config.allowCredentials === true && config.allowOrigins === ALL) {
        throw new Error(`when allowCredentials is true allowOrigins cannot be "*"`);
    }
}

function validateAllowPrivateNetwork(config: CorsConfig) {
    if (config.allowPrivateNetwork === true && config.allowOrigins === ALL) {
        throw new Error(`when allowPrivateNetwork is true allowOrigins cannot be "*"`);
    }
}

function checkOrigin(config: CorsConfig, origin? :string): string | undefined {
    if (origin) {
        const allowedOrigins = config.allowOrigins;
        if (allowedOrigins) {
            if (allowedOrigins === ALL) {
                validateAllowCredentials(config);
                validateAllowPrivateNetwork(config);
                return ALL;
            }
            const originToCheck = trimTrailingSlash(origin.toLowerCase());

            for (const allowedOrigin of allowedOrigins) {
                if ((allowedOrigin ===  ALL) || IOGateway.Filtering.valueMatches(allowedOrigin, originToCheck)) {
                    return origin;
                }
            }
        }
    }
}

function checkMethods(config: CorsConfig, requestMethod?: string): string[] | undefined {
    if (requestMethod) {
        const allowedMethods = config.allowMethods ?? DEFAULT_METHODS;
        if (allowedMethods === ALL) {
            return [requestMethod];
        }
        if (IOGateway.Filtering.valuesMatch(allowedMethods, requestMethod)) {
            return allowedMethods;
        }
    }
}

function checkHeaders(config: CorsConfig, requestHeaders?: string[]): string[] | undefined {
    if (requestHeaders === undefined) {
        return;
    }
    if (requestHeaders.length == 0) {
        return [];
    }
    const allowedHeaders = config.allowHeaders;
    if (allowedHeaders === undefined) {
        return;
    }
    const allowAnyHeader = allowedHeaders === ALL || allowedHeaders.includes(ALL);
    const result: string[] = [];
    for (const requestHeader of requestHeaders) {
        const value = requestHeader?.trim();
        if (value) {
            if (allowAnyHeader) {
                result.push(value);
            }
            else {
                for (const allowedHeader of allowedHeaders) {
                    if (value.toLowerCase() === allowedHeader) {
                        result.push(value);
                        break;
                    }
                }
            }
        }
    }
    if (result.length > 0) {
        return result;
    }
}

function trimTrailingSlash(origin: string): string {
    return origin.endsWith('/') ? origin.slice(0, -1) : origin;
}

function getMethodToUse(request: HttpRequest<ReadonlyHttpHeaders>, isPreFlight: boolean): string | undefined {
    return (isPreFlight ? request.headers.one('access-control-request-method') : request.method);
}

function getHeadersToUse(request: HttpRequest, isPreFlight: boolean): string[] {
    const headers = request.headers;
    return (isPreFlight ? headers.list('access-control-request-headers') : Array.from(headers.keys()));
}

export const matchingCorsConfigSource = (opts: {mappings: Array<[ServerWebExchangeMatcher, CorsConfig | undefined]>}): CorsConfigSource => {
    return async (exchange: ServerWebExchange) => {
        for (const [matcher, config] of opts.mappings) {
            if ((await matcher(exchange)).match) {
                logger.debug(`resolved cors config on '${exchange.request.path}' using ${matcher}: ${JSON.stringify(config)}`);
                return config;
            }
        }
    }
}
